import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { getReaderReadingHistory } from '../services/readerService';
import { getBook } from '../services/bibleService';
import { getFromStorage, STORAGE_KEYS } from '../utils/storageUtils';

function BibleBook() {
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReader, setSelectedReader] = useState('');
  const [readChapters, setReadChapters] = useState([]);
  const [loadingReadHistory, setLoadingReadHistory] = useState(false);
  const navigate = useNavigate();
  const { bookId } = useParams();

  // Fetch book data
  useEffect(() => {
    const fetchBook = async () => {
      if (!bookId) return;
      
      try {
        setLoading(true);
        setError(null);
        console.log(`Fetching Bible book: ${bookId}`);
        const response = await getBook(bookId);
        console.log('Book response received:', response);
        
        // The API returns the book data in a nested 'book' property
        if (response && response.book) {
          setBook(response.book);
        } else {
          setError('Invalid book data received from the server');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch Bible book:', err);
        console.error('Error details:', {
          message: err.message,
          response: err.response ? {
            status: err.response.status,
            statusText: err.response.statusText,
            data: err.response.data
          } : 'No response data',
          request: err.request ? 'Request object exists' : 'No request object'
        });
        setError(`Failed to load Bible book: ${err.message}. Please try again later.`);
        setLoading(false);
      }
    };
    
    fetchBook();
  }, [bookId]);

  // Initialize selected reader from localStorage
  useEffect(() => {
    const savedReader = getFromStorage(STORAGE_KEYS.SELECTED_READER);
    if (savedReader) {
      setSelectedReader(savedReader);
    }
  }, []);

  // Listen for reader changes from the ReaderSelector component
  useEffect(() => {
    const handleReaderChange = (event) => {
      const { readerId } = event.detail;
      console.log('BibleBook - Reader changed event received:', readerId);
      setSelectedReader(readerId);
    };
    
    window.addEventListener('readerChanged', handleReaderChange);
    
    return () => {
      window.removeEventListener('readerChanged', handleReaderChange);
    };
  }, []);

  // Fetch reading history when a reader is selected
  useEffect(() => {
    const fetchReadingHistory = async () => {
      if (!selectedReader) return;
      
      try {
        setLoadingReadHistory(true);
        const response = await getReaderReadingHistory(selectedReader);
        
        // Extract the chapter IDs from the reading history
        const chaptersRead = response.chaptersRead || [];
        setReadChapters(chaptersRead.map(entry => ({
          book: entry.book,
          chapter: entry.chapter,
          chapterId: entry.chapter_id,
          readDate: new Date(entry.read_date || entry.readDate || Date.now()),
          hasNotes: entry.notes && entry.notes.trim().length > 0
        })));
        
        setLoadingReadHistory(false);
      } catch (err) {
        console.error('Failed to fetch reading history:', err);
        setLoadingReadHistory(false);
      }
    };
    
    fetchReadingHistory();
  }, [selectedReader]);

  // Handle chapter selection
  const handleChapterSelect = (bookName, chapterNum) => {
    console.log(`Selecting chapter: ${bookName} ${chapterNum}`);
    const targetUrl = `/bible/${bookName}/${chapterNum}`;
    console.log(`Navigating to: ${targetUrl}`);
    navigate(targetUrl);
  };

  // Calculate the age of a read entry in days
  const calculateReadAge = (readDate) => {
    const now = new Date();
    const diffTime = Math.abs(now - readDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Calculate color based on read date (solid green for 30 days, then fading to white over remaining 335 days)
  const calculateReadColor = (readDate) => {
    const age = calculateReadAge(readDate);
    const solidGreenDays = 30; // Stay solid green for 30 days
    const maxAge = 365; // 365 days = 1 year
    
    // If less than 30 days old, use solid green
    if (age <= solidGreenDays) {
      return '#4caf50'; // Solid green
    }
    
    // Calculate how "fresh" the reading is after the solid period
    // 1.0 = just past solid period, 0.0 = read a year ago or more
    const adjustedAge = age - solidGreenDays;
    const remainingFadePeriod = maxAge - solidGreenDays;
    const freshness = Math.max(0, 1 - (adjustedAge / remainingFadePeriod));
    
    // Calculate RGB values for the gradient from green (#4caf50) to white (#ffffff)
    const r = Math.round(76 + (255 - 76) * (1 - freshness));
    const g = Math.round(175 + (255 - 175) * (1 - freshness));
    const b = Math.round(80 + (255 - 80) * (1 - freshness));
    
    return `rgb(${r}, ${g}, ${b})`;
  };

  // Determine if text should be dark or light based on background color
  const getTextColorForBackground = (backgroundColor) => {
    // For solid green or known colors
    if (backgroundColor === '#4caf50') {
      return 'white';
    }
    
    // For RGB values, extract components
    const rgbMatch = backgroundColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1]);
      const g = parseInt(rgbMatch[2]);
      const b = parseInt(rgbMatch[3]);
      
      // Calculate relative luminance (perceived brightness)
      // Formula: 0.299*R + 0.587*G + 0.114*B
      const brightness = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      
      // Use dark text for bright backgrounds, light text for dark backgrounds
      // Lowering the threshold to ensure better contrast
      return brightness > 0.5 ? '#000000' : '#ffffff';
    }
    
    // Default to black text
    return '#000000';
  };

  // Check if a chapter has been read and get its read date
  const getChapterReadInfo = (bookName, chapterNum) => {
    const readChapter = readChapters.find(
      chapter => chapter.book === bookName && parseInt(chapter.chapter) === chapterNum
    );
    
    if (readChapter) {
      const backgroundColor = calculateReadColor(readChapter.readDate);
      return {
        isRead: true,
        readDate: readChapter.readDate,
        color: backgroundColor,
        textColor: getTextColorForBackground(backgroundColor),
        hasNotes: readChapter.hasNotes
      };
    }
    
    return { isRead: false };
  };

  // Generate chapter grid for a book
  const renderChapterGrid = (bookData) => {
    console.log('Rendering chapter grid for book:', bookData);
    
    if (!bookData || !bookData.chapters || typeof bookData.chapters !== 'number') {
      console.error('Invalid book data for chapter grid:', bookData);
      return <div className="alert alert-warning">No chapter data available</div>;
    }
    
    const chapters = Array.from({ length: bookData.chapters }, (_, i) => i + 1);
    console.log(`Generated ${chapters.length} chapters for ${bookData.name}`);
    
    return (
      <div className="chapter-grid">
        {chapters.map(chapterNum => {
          const readInfo = getChapterReadInfo(bookData.name, chapterNum);
          
          return (
            <button
              key={chapterNum}
              className={`chapter-button ${readInfo.isRead ? 'read' : ''} ${readInfo.hasNotes ? 'has-notes' : ''}`}
              onClick={() => handleChapterSelect(bookData.name, chapterNum)}
              style={readInfo.isRead ? { 
                backgroundColor: readInfo.color,
                color: readInfo.textColor,
                fontWeight: 'bold'
              } : {}}
              title={readInfo.hasNotes ? `${bookData.name} ${chapterNum} - Has notes` : `${bookData.name} ${chapterNum}`}
            >
              {chapterNum}
              {readInfo.hasNotes && (
                <span className="notes-indicator"></span>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading book data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  if (!book) {
    return (
      <div className="alert alert-warning" role="alert">
        No book found. Please select a valid book.
      </div>
    );
  }

  console.log('Rendering BibleBook component with book data:', book);

  return (
    <div className="bible-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="bible-title">{book.name}</h1>
        <Link to="/bible" className="btn btn-outline-secondary">
          <i className="bi bi-arrow-left"></i> Back to All Books
        </Link>
      </div>
      
      {/* Reading Progress Info */}
      {loadingReadHistory && (
        <div className="text-center mt-2 mb-4">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading reading history...</span>
          </div>
          <span className="ms-2">Loading reading history...</span>
        </div>
      )}
      
      {selectedReader && readChapters.length > 0 && (
        <div className="reading-progress mb-4">
          <div className="d-flex align-items-center">
            <div className="chapter-button read me-2" style={{ width: '20px', height: '20px' }}></div>
            <span>= Read chapters</span>
          </div>
        </div>
      )}
      
      {/* Reading Reports Link */}
      {selectedReader && (
        <div className="reading-reports-link mb-4">
          <Link to={`/reading-reports/${selectedReader}`} className="btn btn-primary">
            View Full Reading Reports
          </Link>
        </div>
      )}
      
      <div className="bible-content">
        <div className="book-item">
          <h3 className="book-title">{book.name}</h3>
          <p className="book-info">
            Testament: {book.testament} | Chapters: {book.chapters}
          </p>
          
          {/* Debug info - remove in production */}
          {process.env.NODE_ENV !== 'production' && (
            <div className="debug-info mb-3 p-2 bg-light border">
              <h6>Debug Info:</h6>
              <pre style={{ fontSize: '0.8rem' }}>
                {JSON.stringify(book, null, 2)}
              </pre>
            </div>
          )}
          
          {renderChapterGrid(book)}
        </div>
      </div>
    </div>
  );
}

export default BibleBook; 